/**
 * Enhanced Error Boundaries with TypeScript and Modern React Patterns
 *
 * This file provides comprehensive error boundary components with proper
 * TypeScript typing, error reporting, and recovery mechanisms.
 */

import React, { Component, useState, useEffect } from 'react';
import type { ErrorInfo, ReactNode } from 'react';

import type { ErrorBoundaryProps, ErrorDetails } from '@/types';

// Error boundary state interface
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorId: string | null;
}

/**
 * Base Error Boundary Class Component
 */
class BaseErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  public state: ErrorBoundaryState;
  public props: ErrorBoundaryProps;
  public setState: React.Component<ErrorBoundaryProps, ErrorBoundaryState>['setState'];
  private retryTimeoutId: NodeJS.Timeout | null = null;

  constructor(props: ErrorBoundaryProps) {
    super(props);

    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorId: `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Note: Removed console.error to comply with no-console ESLint rule
    // In production, errors should be logged to a proper logging service

    // Update state with error info
    this.setState({
      errorInfo,
    });

    // Report error if callback provided
    if (this.props.onError) {
      const errorDetails: ErrorDetails = {
        error_type: 'react_error_boundary',
        error_message: error.message,
        session_id: this.props.sessionId || 'unknown',
        user_id: this.props.userId || 'unknown',
        context: `${this.props.name}_error_boundary`,
        stack_trace: error.stack || '',
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
      };

      this.props.onError(errorDetails);
    }

    // Report to external error tracking service in production
    if (process.env.NODE_ENV === 'production') {
      this.reportErrorToService(error, errorInfo);
    }
  }

  componentWillUnmount(): void {
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
    }
  }

  private reportErrorToService = (error: Error, errorInfo: ErrorInfo): void => {
    // In a real application, you would send this to your error tracking service
    // Example: Sentry, Bugsnag, LogRocket, etc.
    try {
      const errorReport = {
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent,
        userId: this.props.userId,
        sessionId: this.props.sessionId,
        boundaryName: this.props.name,
      };

      // Example API call to error reporting service
      fetch('/api/errors/report', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(errorReport),
      }).catch(_reportError => {
        // Note: Removed console.error to comply with no-console ESLint rule
        // In production, this should be logged to a proper logging service
      });
    } catch {
      // Note: Removed console.error to comply with no-console ESLint rule
      // In production, this should be logged to a proper logging service
    }
  };

  private handleRetry = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
    });
  };

  private handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Custom fallback UI
      return (
        <ErrorFallback
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          errorId={this.state.errorId}
          boundaryName={this.props.name}
          onRetry={this.handleRetry}
          onReload={this.handleReload}
        />
      );
    }

    return this.props.children;
  }
}

/**
 * Error Fallback Component
 */
interface ErrorFallbackProps {
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorId: string | null;
  boundaryName: string;
  onRetry: () => void;
  onReload: () => void;
}

const ErrorFallback: React.FC<ErrorFallbackProps> = ({
  error,
  errorInfo,
  errorId,
  boundaryName,
  onRetry,
  onReload,
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const [copied, setCopied] = useState(false);

  const copyErrorDetails = async (): Promise<void> => {
    const errorDetails = {
      errorId,
      boundaryName,
      message: error?.message,
      stack: error?.stack,
      componentStack: errorInfo?.componentStack,
      timestamp: new Date().toISOString(),
      url: window.location.href,
    };

    try {
      await navigator.clipboard.writeText(
        JSON.stringify(errorDetails, null, 2)
      );
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Note: Removed console.error to comply with no-console ESLint rule
      // In production, this should be logged to a proper logging service
    }
  };

  return (
    <div className='min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8'>
      <div className='max-w-md w-full space-y-8'>
        <div className='text-center'>
          <div className='mx-auto h-12 w-12 text-red-500'>
            <svg fill='none' viewBox='0 0 24 24' stroke='currentColor'>
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z'
              />
            </svg>
          </div>
          <h2 className='mt-6 text-3xl font-extrabold text-gray-900'>
            Something went wrong
          </h2>
          <p className='mt-2 text-sm text-gray-600'>
            We encountered an unexpected error in the {boundaryName} component.
          </p>
          {errorId && (
            <p className='mt-1 text-xs text-gray-500'>Error ID: {errorId}</p>
          )}
        </div>

        <div className='space-y-4'>
          <div className='flex space-x-3'>
            <button
              onClick={onRetry}
              className='flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors'
            >
              Try Again
            </button>
            <button
              onClick={onReload}
              className='flex-1 bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors'
            >
              Reload Page
            </button>
          </div>

          <button
            onClick={() => setShowDetails(!showDetails)}
            className='w-full text-sm text-gray-500 hover:text-gray-700 focus:outline-none'
          >
            {showDetails ? 'Hide' : 'Show'} Error Details
          </button>

          {showDetails && (
            <div className='bg-gray-100 p-4 rounded-md text-left'>
              <div className='flex justify-between items-center mb-2'>
                <h4 className='text-sm font-medium text-gray-900'>
                  Error Details
                </h4>
                <button
                  onClick={copyErrorDetails}
                  className='text-xs text-blue-600 hover:text-blue-800 focus:outline-none'
                >
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>

              {error && (
                <div className='mb-3'>
                  <p className='text-xs font-medium text-gray-700'>Message:</p>
                  <p className='text-xs text-gray-600 font-mono bg-white p-2 rounded border'>
                    {error.message}
                  </p>
                </div>
              )}

              {process.env.NODE_ENV === 'development' && error?.stack && (
                <div className='mb-3'>
                  <p className='text-xs font-medium text-gray-700'>
                    Stack Trace:
                  </p>
                  <pre className='text-xs text-gray-600 font-mono bg-white p-2 rounded border overflow-auto max-h-32'>
                    {error.stack}
                  </pre>
                </div>
              )}

              {process.env.NODE_ENV === 'development' &&
                errorInfo?.componentStack && (
                  <div>
                    <p className='text-xs font-medium text-gray-700'>
                      Component Stack:
                    </p>
                    <pre className='text-xs text-gray-600 font-mono bg-white p-2 rounded border overflow-auto max-h-32'>
                      {errorInfo.componentStack}
                    </pre>
                  </div>
                )}
            </div>
          )}
        </div>

        <div className='text-center'>
          <p className='text-xs text-gray-500'>
            If this problem persists, please contact support with the error ID
            above.
          </p>
        </div>
      </div>
    </div>
  );
};

/**
 * Specialized Error Boundaries for different parts of the application
 */

// App-level error boundary
export const AppErrorBoundary: React.FC<ErrorBoundaryProps> = props => (
  <BaseErrorBoundary {...props} name={props.name || 'Application'} />
);

// Chat-specific error boundary
export const ChatErrorBoundary: React.FC<ErrorBoundaryProps> = props => (
  <BaseErrorBoundary {...props} name={props.name || 'Chat'} />
);

// Auth-specific error boundary
export const AuthErrorBoundary: React.FC<ErrorBoundaryProps> = props => (
  <BaseErrorBoundary {...props} name={props.name || 'Authentication'} />
);

// Component-level error boundary
export const ComponentErrorBoundary: React.FC<ErrorBoundaryProps> = props => (
  <BaseErrorBoundary {...props} name={props.name || 'Component'} />
);

/**
 * Hook for handling errors in functional components
 */
export const useErrorHandler = () => {
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (error) {
      throw error;
    }
  }, [error]);

  const handleError = (error: Error) => {
    setError(error);
  };

  const clearError = () => {
    setError(null);
  };

  return { handleError, clearError };
};

/**
 * Higher-order component for adding error boundary
 */
export const withErrorBoundary = <P extends object>(
  Component: React.ComponentType<P>,
  errorBoundaryProps?: Partial<ErrorBoundaryProps>
) => {
  const WrappedComponent: React.FC<P> = props => (
    <BaseErrorBoundary
      name={`${Component.displayName || Component.name || 'Component'}ErrorBoundary`}
      {...errorBoundaryProps}
    >
      <Component {...props} />
    </BaseErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;

  return WrappedComponent;
};

// Export the base error boundary as default
export default BaseErrorBoundary;
